import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { ModalControlsSaveable } from '®shared/modals';
import {
  Permissions,
  UserPermissions,
  hasPermissionForListId,
} from '®shared/permissions';
import {
  EditableList,
  ListDetailsFormModel,
} from '®shared/reading-list/models';
import { License, ListAccess, Period } from '®shared/tenant';

import {
  ListDetailsEditorHideModalAction,
  ListDetailsEditorSaveAction,
} from './list-details-editor.actions';

@Component({
  selector: 'rl-list-details-editor',
  templateUrl: './list-details-editor.component.html',
})
export class ListDetailsEditorComponent implements OnChanges {
  @Input() editableList: EditableList;
  @Input() currentUserPermissions: UserPermissions;
  @Input() licenses: License[];
  @Input() timePeriods: Period[];
  @Input() modalControl: ModalControlsSaveable;

  @Output() save = new EventEmitter<ListDetailsEditorSaveAction>();
  @Output() close = new EventEmitter<ListDetailsEditorHideModalAction>();

  ListAccess = ListAccess;

  canAssignLicenses: boolean;
  canSetPrivacy: boolean;
  tenantDefaultLicense: License;
  listDetailsForm: FormGroup;
  listDetailsModel: ListDetailsFormModel;

  constructor(private formBuilder: FormBuilder) {
    this.createForm();
    this.updateModelOnFormChanges();
  }

  createForm() {
    const minOneNonWhiteSpaceValidator = Validators.pattern(/\S+/);
    this.listDetailsForm = this.formBuilder.group({
      title: [null, [Validators.required, minOneNonWhiteSpaceValidator]],
      description: [null, [minOneNonWhiteSpaceValidator]],
      internalNote: [null, [minOneNonWhiteSpaceValidator]],
      license: null,
      timePeriod: null,
      listAccess: null,
    });
  }

  updateModelOnFormChanges() {
    this.listDetailsForm.valueChanges.forEach(latestModel => {
      this.listDetailsModel = Object.assign(
        new ListDetailsFormModel(),
        latestModel,
      );
    });
  }

  licenseComparator(license1: License, license2: License): boolean {
    return license1 && license2
      ? license1.id === license2.id
      : license1 === license2;
  }

  timePeriodComparator(period1: Period, period2: Period): boolean {
    return period1 && period2 ? period1.id === period2.id : period1 === period2;
  }

  ngOnChanges() {
    if (this.currentUserPermissions) {
      this.canAssignLicenses = hasPermissionForListId(
        Permissions.ASSIGN_LICENSE,
        this.editableList.id,
        this.currentUserPermissions,
      );
      this.canSetPrivacy = hasPermissionForListId(
        Permissions.LIST_ACCESS_CONTROL,
        this.editableList.id,
        this.currentUserPermissions,
      );
    }

    if (this.licenses) {
      this.tenantDefaultLicense = this.licenses.find(
        license => license.is_tenant_default,
      );
    }

    if (!this.listDetailsModel) {
      this.listDetailsModel = new ListDetailsFormModel(this.editableList);
      this.listDetailsForm.setValue(this.listDetailsModel);
    }

    if (!this.modalControl.isShown) {
      this.listDetailsForm.reset(new ListDetailsFormModel(this.editableList));
    }
  }

  onCancel() {
    if (this.modalControl.isSaving) {
      return;
    }

    this.close.emit(new ListDetailsEditorHideModalAction());
  }

  onSubmit() {
    if (this.modalControl.isSaving || this.listDetailsForm.invalid) {
      return;
    }

    this.save.emit(
      new ListDetailsEditorSaveAction({
        listId: this.editableList.id,
        listDetails: this.listDetailsModel,
      }),
    );
  }

  validationErrors(): ValidationErrors[] {
    return Object.keys(this.listDetailsForm.controls)
      .map(name => this.listDetailsForm.controls[name].errors)
      .filter(error => error !== null);
  }

  get titleCharactersRemaining(): number {
    return 100 - (this.listDetailsForm.controls.title.value?.length || 0);
  }
}
